import { useEffect } from 'react'
import BottomSheetContent from '../../../../../../components/BottomSheetComponents/BottomSheetContent'
import { SecondaryButton } from '../../../../../../components/execution/SecondaryButton'
import { IdealPlanImportantNote } from '../../../lumpsum/plan-bottom-sheet/ideal/header/IdealPlanImportantNote'
import { IdealPlanManagerTrackRecordDisclaimer } from '../../../lumpsum/plan-bottom-sheet/ideal/body/IdealPlanManagerTrackRecordDisclaimer'
import { IdealPlanRecommendationSkeleton } from '../../../lumpsum/plan-bottom-sheet/ideal/header/IdealPlanRecommendationSkeleton'
import {
  PlanRecommendationFundTile,
  RECOMMENDATION_SINGLE_FUND_CARD_WIDTH_CLASS
} from '../../../lumpsum/plan-bottom-sheet/shared/funds/PlanRecommendationFundTile'
import type { SipIdealPlanRecommendationApiResponse } from '../api/idealRecommendation.types'
import { hasIdealPlanScoreOrRankReasonPointers } from '../../../lumpsum/plan-bottom-sheet/ideal/body/IdealPlanReasonPointers'
import { IdealSipPlanReasonsToInvest } from './body/IdealSipPlanReasonPointers'
import { IdealSipPlanRecommendationHero } from './header/IdealSipPlanRecommendationHero'
import { openRecommendationFundDetail } from '../../../lumpsum/plan-bottom-sheet/open/planRecommendationOpen'
import { EventNames } from '../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../utils/appBridge'
import { PlansBottomSheet, PlansFundRole, PlansScreen, PlansSipSheetEventIds } from '../../../analytics/plansEvents'
import { trackPlansSipFundTileOpen } from '../../../analytics/plansTrack'

const MANAGER_TRACK_RECORD_DISCLAIMER =
  "Our ratings also account for the fund manager's track record and performance"

type IdealSipPlanRecommendationSheetProps = {
  onDismiss: () => void
  planDetails: SipIdealPlanRecommendationApiResponse | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
}

export function IdealSipPlanRecommendationSheet({
  onDismiss,
  planDetails,
  isLoading,
  isFetchFailed,
  onRetry
}: IdealSipPlanRecommendationSheetProps) {
  useEffect(() => {
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        ScreenName: PlansScreen.SipIdealRecommendation,
        eventId: PlansBottomSheet.SipRecommendation,
        bottomSheetName: PlansBottomSheet.SipRecommendation
      }
    })
  }, [])

  function handleClose() {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.SipIdealRecommendation,
        eventId: PlansSipSheetEventIds.Close,
        buttonLabel: 'Close'
      }
    })
    onDismiss()
  }

  return (
    <BottomSheetContent
      onDismiss={handleClose}
      heading={undefined}
      bottomSheetHeadClassName="!pt-6"
      bottomSheetBodyClassName="!px-0 !pb-2 !overflow-y-auto no-scrollbar"
      bottomSheetCtas={(
        <SecondaryButton
          label="Close"
          onClick={handleClose}
          secondaryButtonClassName="!h-[56px] !text-[16px]"
        />
      )}
    >
      {isLoading ? (
        <IdealPlanRecommendationSkeleton />
      ) : isFetchFailed ? (
        <IdealSipPlanRecommendationSheetErrorState onRetry={onRetry} />
      ) : (
        <>
          <IdealSipPlanRecommendationHero planDetails={planDetails} />
          <PlanRecommendationFundTile
            mfLogoUrl={planDetails?.mfLogoUrl ?? ''}
            fundName={planDetails?.fundName ?? ''}
            fundNature={planDetails?.fundNature ?? ''}
            fundCategory={planDetails?.fundCategory ?? ''}
            powerRating={planDetails?.powerRating ?? ''}
            powerRank={planDetails?.powerRank ?? null}
            purchaseMode={planDetails?.purchaseMode ?? ''}
            fundIsin={planDetails?.isin ?? ''}
            widthClassName={RECOMMENDATION_SINGLE_FUND_CARD_WIDTH_CLASS}
            heightClassName="h-max !py-4"
            outerClassName="px-5 pt-6"
            onCardClick={() => {
              trackPlansSipFundTileOpen({
                screenName: PlansScreen.SipIdealRecommendation,
                fundRole: PlansFundRole.Ideal,
                fundName: planDetails?.fundName ?? '',
                fundIsin: planDetails?.isin ?? ''
              })
              openRecommendationFundDetail(planDetails?.isin ?? '')
            }}
          />
          <IdealPlanImportantNote />
          <div className="mt-6 h-[10px] bg-[#F0F4F7]" />
          <IdealSipPlanReasonsToInvest planDetails={planDetails} />
          {hasIdealPlanScoreOrRankReasonPointers(planDetails) ? (
            <IdealPlanManagerTrackRecordDisclaimer disclaimer={MANAGER_TRACK_RECORD_DISCLAIMER} />
          ) : null}
        </>
      )}
    </BottomSheetContent>
  )
}

export function IdealSipPlanRecommendationSheetErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-5 py-10">
      <div className="flex w-full max-w-[279px] flex-col items-center gap-7">
        <div className="flex flex-col items-center gap-5">
          <div className="text-[22px] font-[500] leading-[130%] text-[#232323]">Something went wrong</div>
          <div className="text-center text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
            We couldn&apos;t load this ideal SIP recommendation right now.
          </div>
          <div
            className="mt-1 inline-flex !h-10 w-[120px] cursor-pointer items-center justify-center rounded-xl bg-[#232323] text-[14px] font-[500] text-white"
            onClick={() => {
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  ScreenName: PlansScreen.SipIdealRecommendation,
                  eventId: PlansSipSheetEventIds.Retry,
                  buttonLabel: 'Retry'
                }
              })
              onRetry()
            }}
          >
            Retry
          </div>
        </div>
      </div>
    </div>
  )
}
